import { useEffect, useRef, useState } from 'react';
import NextLink from 'next/link';
import classnames from 'classnames';
import { DesktopNavProps, HeaderProps } from 'shared/types/nav';
import { useNavContext } from 'context/nav';
import { useStudioDataContext } from 'context/studioData';
import { useFormat } from 'helpers/hooks/useFormat';
import { HEADER } from 'helpers/utils/googleAnalyticsEvents';
import { useAnalytics } from 'hooks/useAnalytics';
import useNavData from 'hooks/useNavData';
import { FEATURE_FLAG_LIST, useFeatureFlags } from 'hooks/useFeatureFlags';
import { mutate } from 'swr';
import { sdk } from 'sdk';
import Logger from 'helpers/logger';
import { useAccount } from 'frontastic';
import { DetailedNav } from './detailed-nav';
import styles from './mega-nav.module.scss';
import { PrimaryNav } from './primary-nav';
import { SecondaryNav } from './secondary-nav';
import { SiteLogo } from './site-logo';
import { TopNavDesktopBar } from './top-nav-desktop';

export const DesktopNav = ({ navData, menuLength }: DesktopNavProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const { trackEvent, EVENT_CATEGORY } = useAnalytics();

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;
      setIsSticky(window.scrollY > headerRef.current?.offsetTop);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky]);

  const handleLogoClick = () => {
    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, HEADER.TOP_NAV_LOGO_CLICK);
  };

  return (
    <>
      <div className={styles.topNavWrapperDesktop}>
        <TopNavDesktopBar />
      </div>
      <div id={isSticky ? 'fixedHeader' : ''} ref={headerRef} className={classnames({ [styles.isSticky]: isSticky })}>
        <div className={styles.navContainerDesktop}>
          <div className={styles.navWrapperDesktop}>
            <div className={styles.logo}>
              <SiteLogo logoLink="/" onClick={handleLogoClick} />
            </div>
            <PrimaryNav navData={navData} menuLength={menuLength} />
            <SecondaryNav />
          </div>
        </div>
        <DetailedNav />
      </div>
    </>
  );
};

const HeaderContainer = ({ categories, studioData }: HeaderProps) => {
  const { setData } = useStudioDataContext();

  useEffect(() => {
    setData((prev) => ({ ...prev, navData: studioData }));
  }, [studioData]);
  const { navData, menuLength } = useNavData({ categories, studioData });

  const { isDetailedNavOpen } = useNavContext();
  const { isUserLoggedIn } = useAccount();
  const { isFeatureEnabled, isFeatureFlagDataAvailable } = useFeatureFlags();
  const isBusinessUnitInfoFixEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.FETCH_BUSINESS_UNIT_INFO_API_FIX);
  const { formatMessage } = useFormat({ name: 'common' });

  useEffect(() => {
    if (!isUserLoggedIn || !isFeatureFlagDataAvailable || !isBusinessUnitInfoFixEnabled) return;

    void mutate(
      '/action/businessUnit/getBusinessUnitInfo',
      () => sdk.callAction({ actionName: 'businessUnit/fetchBusinessUnitInfo' }),
      false,
    ).catch((error) => Logger.error(`Failed to refresh header business unit: ${String(error)}`));
  }, [isUserLoggedIn, isFeatureFlagDataAvailable, isBusinessUnitInfoFixEnabled]);

  useEffect(() => {
    // Set focus to the top of the page
    document?.getElementById('top-of-page')?.focus();
  }, []);

  if (!isUserLoggedIn) {
    return null;
  }

  return (
    <header
      className={classnames(styles.megaNav, {
        [styles.open]: isDetailedNavOpen,
      })}
    >
      <NextLink className={styles.skipNav} href="#main-content">
        {formatMessage({ id: 'skipNavigation', defaultMessage: 'Skip Navigation' })}
      </NextLink>
      <nav className={styles.mainNav} aria-label="Main Navigation">
        <DesktopNav categories={categories} navData={navData} menuLength={menuLength} />
      </nav>
    </header>
  );
};

export default HeaderContainer;
